import { type FormEvent, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button, Card, Field, Input, Logo, PetalField } from '../design';
import { ApiError } from '../lib/api';
import './LoginPage.css';

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = (location.state as LocationState | null)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    return <Navigate to={dest} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'Incorrect email or password.'
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="zen-login"
      style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1fr', background: 'var(--surface-page)' }}
    >
      <div
        className="zen-login__brand"
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--navy-600)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: 'var(--space-9)',
        }}
      >
        <PetalField size={520} blur={3} style={{ position: 'absolute', right: -140, top: -120, opacity: 0.5 }} />
        <Logo theme="white" height={36} />
        <div style={{ position: 'relative' }}>
          <div style={{ font: 'var(--fw-regular) 44px/1.2 var(--font-display)', color: 'var(--white)', maxWidth: '16ch' }}>
            Change happens below the surface.
          </div>
          <p style={{ font: 'var(--type-body)', color: 'var(--navy-100)', maxWidth: '38ch', marginTop: 'var(--space-6)' }}>
            Your practice, your programs and your sessions, in one place.
          </p>
        </div>
        <span style={{ position: 'relative', font: 'var(--type-caption)', color: 'var(--navy-200)' }}>zenmindware.com</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-8)' }}>
        <Card variant="raised" pad="lg" style={{ width: 400, maxWidth: '100%' }}>
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }} noValidate>
            <div>
              <h2 style={{ font: 'var(--type-h3)' }}>Sign in</h2>
              <p style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)', margin: '6px 0 0' }}>
                Welcome back to the Practitioners' Portal.
              </p>
            </div>
            <Field label="Email">
              <Input
                type="email"
                icon="mail"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@zenmindware.com"
              />
            </Field>
            <Field label="Password" error={error ?? undefined}>
              <Input
                type="password"
                icon="lock"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </Field>
            <Button block type="submit" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
            <p style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', margin: 0, textAlign: 'center' }}>
              Trouble signing in? Contact your administrator.
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
}
